import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Modal } from '@/components/common/AppModal';
import { Alert, Button, Form, Spinner } from 'react-bootstrap';
import IconAlert from '@/components/common/IconAlert';
import { getIndexerErrorInfo } from '../api/errors';
import { indexerAlertVariant } from '../lib/indexer-alert-variant';
import {
  useCreateInspectScheduleMutation,
  useDeleteInspectScheduleMutation,
  useLazyListInspectSchedulesQuery,
  usePatchInspectScheduleMutation,
} from '../api/indexerApi';
import type {
  InspectScheduleItem,
  ProjectJobState,
  ScheduleMessenger,
} from '../api/types';
import { jobStateLabel } from '../lib/job-labels';
import { ScheduleListTable } from './ScheduleListTable';
import { SchedulePanelPreload } from './preloads/IndexerPreloads';
import { ScheduleDeleteConfirmModal } from './ScheduleDeleteConfirmModal';
import {
  DEFAULT_SCHEDULE_MESSENGERS,
  ScheduleChannelBotsAlert,
  ScheduleMessengerSelect,
  filterJoinedScheduleMessengers,
  formatScheduleMessengersLabel,
} from './ScheduleMessengerSelect';
import { useGetUserInfoQuery } from '@/app/common/api/authApi';
import { SitemapUrlImport } from './SitemapUrlImport';
import { InspectScheduleWebhookForm } from './InspectScheduleWebhookForm';
import {
  crossScheduleWarnings,
  findDuplicateLines,
  normalizeLines,
} from '../lib/schedule-duplicates';
import { formatWebhookBucketFilters } from '../lib/webhook-bucket-filters';
import {
  ensureWebhookBucketFilters,
  hasInspectPerUrlWebhook,
} from '../lib/inspect-webhook-events';
import {
  applyDefaultInspectScheduleSettings,
  inferInspectScheduleSettingsMode,
  RECOMMENDED_SETTINGS_ALERT,
  RECOMMENDED_SETTINGS_LABEL,
  type InspectScheduleSettingsMode,
} from '../lib/inspect-schedule-settings';
import { formatFaNumber, formatTime, mergeUrlListText, parseUrlList, projectUrlListPlaceholder } from '../lib/utils';
import { useProject } from '../lib/project-context';
import type { InspectScheduleCreatePrefill } from '../lib/inspect-schedule-prefill';
import { cleanupOrphanModalBackdrops } from '../lib/modal-body-cleanup';

import {
  ALL_EXCLUDED_SUB_FILTER_IDS,
} from '../lib/excluded-coverage-filters';


function normalizeSavedWebhookFilters(filters: string[]): string[] {
  const buckets = new Set(['indexed', 'excluded', 'error']);
  const allowedSubs = new Set<string>(ALL_EXCLUDED_SUB_FILTER_IDS);
  return filters.filter(
    (value) => buckets.has(value) || allowedSubs.has(value),
  );
}

function formatRequestError(err: unknown, fallback: string): string {
  const info = getIndexerErrorInfo(err);
  return `HTTP ${info.code} — ${info.message || fallback}`;
}

export function InspectSchedulePanel({
  projectId,
  createPrefill,
  onCreatePrefillConsumed,
}: {
  projectId: string;
  createPrefill?: InspectScheduleCreatePrefill | null;
  onCreatePrefillConsumed?: () => void;
}) {
  const { project } = useProject();
  const { data: userInfo } = useGetUserInfoQuery();
  const [schedules, setSchedules] = useState<InspectScheduleItem[]>([]);
  const [modalOpen, setModalOpen] = useState(false);
  const [isNew, setIsNew] = useState(false);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [name, setName] = useState('');
  const [enabled, setEnabled] = useState(false);
  const [urlText, setUrlText] = useState('');
  const [webhookFilters, setWebhookFilters] = useState<string[]>(
    () => applyDefaultInspectScheduleSettings().filters,
  );
  const [webhookEvents, setWebhookEvents] = useState<string[]>(
    () => applyDefaultInspectScheduleSettings().events,
  );
  const [autoIndexNotIndexed, setAutoIndexNotIndexed] = useState(
    () => applyDefaultInspectScheduleSettings().autoIndexNotIndexed,
  );
  const [settingsMode, setSettingsMode] =
    useState<InspectScheduleSettingsMode>('default');
  const [messengers, setMessengers] = useState<ScheduleMessenger[]>(
    DEFAULT_SCHEDULE_MESSENGERS,
  );
  const [lastRunAt, setLastRunAt] = useState<string | null>(null);
  const [lastRunStatus, setLastRunStatus] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState<{ variant: string; text: string } | null>(
    null,
  );
  const [modalMessage, setModalMessage] = useState<{
    variant: string;
    text: string;
  } | null>(null);
  const [warnings, setWarnings] = useState<string[]>([]);
  const [deleteTargetId, setDeleteTargetId] = useState<string | null>(null);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [deleteBusy, setDeleteBusy] = useState(false);
  const [resumeEditOnDeleteCancel, setResumeEditOnDeleteCancel] = useState(false);
  const pendingDeleteIdRef = useRef<string | null>(null);
  const openDeleteAfterEditExitRef = useRef(false);
  const resumeEditAfterDeleteExitRef = useRef(false);
  const [listInspectSchedules] = useLazyListInspectSchedulesQuery();
  const [createInspectSchedule] = useCreateInspectScheduleMutation();
  const [patchInspectSchedule] = usePatchInspectScheduleMutation();
  const [deleteInspectSchedule] = useDeleteInspectScheduleMutation();

  const loadForm = useCallback((item: InspectScheduleItem | null, asNew = false) => {
    if (asNew || !item) {
      const defaults = applyDefaultInspectScheduleSettings();
      setIsNew(true);
      setSelectedId(null);
      setName('زمان‌بندی جدید');
      setEnabled(true);
      setUrlText('');
      setWebhookFilters(defaults.filters);
      setWebhookEvents(defaults.events);
      setAutoIndexNotIndexed(defaults.autoIndexNotIndexed);
      setSettingsMode('default');
      setMessengers([...DEFAULT_SCHEDULE_MESSENGERS]);
      setLastRunAt(null);
      setLastRunStatus(null);
      return;
    }
    setIsNew(false);
    setSelectedId(item.id);
    setName(item.name);
    setEnabled(item.enabled);
    setUrlText((item.urls ?? []).join('\n'));
    const mode = inferInspectScheduleSettingsMode({
      settingsMode: item.settingsMode,
      webhookEvents: item.webhookEvents,
      autoIndexNotIndexed: item.autoIndexNotIndexed,
    });
    setSettingsMode(mode);
    if (mode === 'default') {
      const defaults = applyDefaultInspectScheduleSettings();
      setWebhookFilters(defaults.filters);
      setWebhookEvents(defaults.events);
      setAutoIndexNotIndexed(defaults.autoIndexNotIndexed);
    } else {
      setWebhookFilters(normalizeSavedWebhookFilters(item.webhookFilters ?? []));
      setWebhookEvents(item.webhookEvents ?? []);
      setAutoIndexNotIndexed(Boolean(item.autoIndexNotIndexed));
    }
    setMessengers(item.messengers ?? []);
    setLastRunAt(item.lastRunAt);
    setLastRunStatus(item.lastRunStatus);
  }, []);

  const openCreate = useCallback(() => {
    setModalMessage(null);
    setWarnings([]);
    loadForm(null, true);
    setModalOpen(true);
  }, [loadForm]);

  const openEdit = useCallback(
    (id: string) => {
      const item = schedules.find((row) => row.id === id);
      if (!item) return;
      setModalMessage(null);
      setWarnings([]);
      loadForm(item);
      setModalOpen(true);
    },
    [schedules, loadForm],
  );

  const closeModal = useCallback(() => {
    if (busy || deleteBusy) return;
    openDeleteAfterEditExitRef.current = false;
    setModalOpen(false);
    setModalMessage(null);
    setWarnings([]);
  }, [busy, deleteBusy]);

  const onEditModalExited = useCallback(() => {
    cleanupOrphanModalBackdrops();
    if (!openDeleteAfterEditExitRef.current) return;
    openDeleteAfterEditExitRef.current = false;
    setDeleteConfirmOpen(true);
  }, []);

  const onDeleteModalExited = useCallback(() => {
    cleanupOrphanModalBackdrops();
    if (!resumeEditAfterDeleteExitRef.current) return;
    resumeEditAfterDeleteExitRef.current = false;
    setModalOpen(true);
  }, []);

  useEffect(() => {
    if (!createPrefill?.urls?.length) return;

    setModalMessage(null);
    setWarnings([]);
    setIsNew(true);
    setSelectedId(null);
    setName(createPrefill.name ?? 'زمان‌بندی جدید');
    setEnabled(true);
    setUrlText(createPrefill.urls.join('\n'));
    setWebhookEvents(createPrefill.webhookEvents);
    setWebhookFilters(normalizeSavedWebhookFilters(createPrefill.webhookFilters));
    setAutoIndexNotIndexed(Boolean(createPrefill.autoIndexNotIndexed));
    setSettingsMode('advanced');
    setMessengers([...DEFAULT_SCHEDULE_MESSENGERS]);
    setLastRunAt(null);
    setLastRunStatus(null);
    setModalOpen(true);
    onCreatePrefillConsumed?.();
  }, [createPrefill, onCreatePrefillConsumed]);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    void listInspectSchedules(projectId, true)
      .unwrap()
      .then((data) => {
        if (cancelled) return;
        setSchedules(data.schedules);
      })
      .catch((err) => {
        if (cancelled) return;
        setMessage({
          variant: 'danger',
          text: formatRequestError(err, 'بارگذاری ناموفق بود'),
        });
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [projectId, listInspectSchedules]);

  const urls = useMemo(() => normalizeLines(urlText), [urlText]);
  const uniqueUrlCount = useMemo(() => parseUrlList(urlText).length, [urlText]);
  const withinDupes = useMemo(() => findDuplicateLines(urls), [urls]);
  const localWarnings = useMemo(() => {
    const others = schedules
      .filter((item) => item.id !== selectedId)
      .map((item) => ({ name: item.name, items: item.urls ?? [] }));
    return crossScheduleWarnings(urls, others, 'آدرس');
  }, [urls, schedules, selectedId]);

  const tableRows = useMemo(
    () =>
      schedules.map((item) => {
        const parts = [`${formatFaNumber(item.urls?.length ?? 0)} آدرس`];
        const notify = formatWebhookBucketFilters(
          item.webhookFilters ?? [],
        ).replace(/ · /g, '، ');
        parts.push(`اطلاع‌رسانی: ${notify}`);
        const messengers = formatScheduleMessengersLabel(item.messengers ?? []);
        if (messengers) parts.push(`کانال: ${messengers}`);
        return {
          id: item.id,
          name: item.name,
          enabled: item.enabled,
          detail: parts.join(' · '),
          lastRunAt: item.lastRunAt,
          lastRunStatus: item.lastRunStatus,
        };
      }),
    [schedules],
  );

  async function onSave() {
    setBusy(true);
    setModalMessage(null);
    setWarnings([]);
    if (withinDupes.length > 0) {
      setModalMessage({
        variant: 'danger',
        text: `آدرس تکراری در همین زمان‌بندی: ${withinDupes.join('، ')}`,
      });
      setBusy(false);
      return;
    }
    try {
      const defaults = applyDefaultInspectScheduleSettings();
      const eventsToSave =
        settingsMode === 'default' ? defaults.events : webhookEvents;
      const autoIndexToSave =
        settingsMode === 'default'
          ? defaults.autoIndexNotIndexed
          : autoIndexNotIndexed;
      const filtersToSave =
        settingsMode === 'default'
          ? defaults.filters
          : hasInspectPerUrlWebhook(eventsToSave)
            ? ensureWebhookBucketFilters(webhookFilters)
            : [];
      const input = {
        name: name.trim(),
        enabled,
        urls,
        webhookFilters: filtersToSave,
        webhookEvents: eventsToSave,
        autoIndexNotIndexed: autoIndexToSave,
        settingsMode,
        messengers: filterJoinedScheduleMessengers(
          settingsMode === 'default'
            ? [...DEFAULT_SCHEDULE_MESSENGERS]
            : messengers,
          userInfo,
        ),
      };
      const result = isNew
        ? await createInspectSchedule({ projectId, ...input }).unwrap()
        : await patchInspectSchedule({
            projectId,
            scheduleId: selectedId!,
            ...input,
          }).unwrap();
      setSchedules((prev) => {
        if (isNew) return [...prev, result.schedule];
        return prev.map((item) =>
          item.id === result.schedule.id ? result.schedule : item,
        );
      });
      setWarnings(result.warnings);
      setModalOpen(false);
      setMessage({
        variant: result.warnings.length > 0 ? 'warning' : 'success',
        text:
          result.warnings.length > 0
            ? `«${result.schedule.name}» ذخیره شد؛ هشدار تکراری بین زمان‌بندی‌ها را در ویرایش ببینید.`
            : `زمان‌بندی «${result.schedule.name}» ذخیره شد.`,
      });
    } catch (err) {
      setModalMessage({
        variant: 'danger',
        text: formatRequestError(err, 'ذخیره ناموفق بود'),
      });
    } finally {
      setBusy(false);
    }
  }

  function requestDelete(id: string) {
    pendingDeleteIdRef.current = id;
    setDeleteTargetId(id);
    setResumeEditOnDeleteCancel(modalOpen && selectedId === id);
    resumeEditAfterDeleteExitRef.current = false;
    if (modalOpen) {
      openDeleteAfterEditExitRef.current = true;
      setModalOpen(false);
      return;
    }
    openDeleteAfterEditExitRef.current = false;
    setDeleteConfirmOpen(true);
  }

  function closeDeleteConfirm() {
    if (deleteBusy) return;
    const resumeEdit = resumeEditOnDeleteCancel;
    pendingDeleteIdRef.current = null;
    setDeleteTargetId(null);
    setResumeEditOnDeleteCancel(false);
    resumeEditAfterDeleteExitRef.current = Boolean(resumeEdit && selectedId);
    setDeleteConfirmOpen(false);
  }

  async function confirmDelete() {
    const targetId = pendingDeleteIdRef.current ?? deleteTargetId;
    if (!targetId) return;
    const targetName =
      schedules.find((item) => item.id === targetId)?.name ?? name;
    setDeleteBusy(true);
    setMessage(null);
    setModalMessage(null);
    try {
      await deleteInspectSchedule({ projectId, scheduleId: targetId }).unwrap();
      setSchedules((prev) => prev.filter((item) => item.id !== targetId));
      pendingDeleteIdRef.current = null;
      openDeleteAfterEditExitRef.current = false;
      resumeEditAfterDeleteExitRef.current = false;
      setResumeEditOnDeleteCancel(false);
      setModalOpen(false);
      setDeleteConfirmOpen(false);
      setDeleteTargetId(null);
      setMessage({
        variant: 'success',
        text: `زمان‌بندی «${targetName}» حذف شد.`,
      });
    } catch (err) {
      openDeleteAfterEditExitRef.current = false;
      resumeEditAfterDeleteExitRef.current = false;
      setResumeEditOnDeleteCancel(false);
      setDeleteConfirmOpen(false);
      setDeleteTargetId(null);
      pendingDeleteIdRef.current = null;
      setMessage({
        variant: 'danger',
        text: formatRequestError(err, 'حذف ناموفق بود'),
      });
    } finally {
      setDeleteBusy(false);
    }
  }

  if (loading) {
    return <SchedulePanelPreload />;
  }

  return (
    <>
      {message && (
        <IconAlert
          variant={indexerAlertVariant(message.variant)}
          dismissible
          onClose={() => setMessage(null)}
          className="mb-3"
        >
          <p className="mb-0 text-justify pe-3">{message.text}</p>
        </IconAlert>
      )}

      <IconAlert variant="info" className="mb-3">
        <p className="mb-0 text-justify pe-3">
          همه زمان‌بندی‌های <strong>فعال</strong> به‌صورت <strong>خودکار</strong> اجرا
          می‌شوند.
        </p>
      </IconAlert>

      <ScheduleListTable
        rows={tableRows}
        detailHeader="محتوا"
        emptyLabel="برای درخواست خودکار، یک زمان‌بندی با لیست آدرس بسازید."
        onCreate={openCreate}
        onEdit={openEdit}
        onDelete={requestDelete}
      />

      <Modal
        show={modalOpen}
        onHide={closeModal}
        onExited={onEditModalExited}
        size="lg"
        scrollable
        centered
        enforceFocus={false}
      >
        <Modal.Header closeButton>
          <Modal.Title className="fs--1 fw-semibold mb-0">
            {isNew ? 'زمان‌بندی جدید — درخواست' : `ویرایش: ${name}`}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {modalMessage && (
            <Alert
              variant={modalMessage.variant}
              dismissible
              onClose={() => setModalMessage(null)}
            >
              {modalMessage.text}
            </Alert>
          )}

          {(warnings.length > 0 || localWarnings.length > 0) && (
            <Alert variant="warning">
              {[...new Set([...localWarnings, ...warnings])].map((warning) => (
                <div key={warning}>{warning}</div>
              ))}
            </Alert>
          )}

          {withinDupes.length > 0 && (
            <Alert variant="danger">
              آدرس تکراری در همین زمان‌بندی: {withinDupes.join('، ')}
            </Alert>
          )}

          <Form.Group className="mb-3">
            <Form.Label>نام زمان‌بندی</Form.Label>
            <Form.Control value={name} onChange={(e) => setName(e.target.value)} />
          </Form.Group>

          <Form.Group className="mb-3">
            <div className="d-flex flex-wrap justify-content-between align-items-center gap-2 mb-2">
              <Form.Label className="mb-0">
                آدرس‌ها برای درخواست (هر خط یک آدرس)
              </Form.Label>
              {project && (
                <SitemapUrlImport
                  projectId={projectId}
                  project={project}
                  existingUrls={urls}
                  onImport={(selected) => {
                    if (selected.length === 0) return;
                    setUrlText(mergeUrlListText(urlText, selected));
                    setModalMessage({
                      variant: 'success',
                      text: `${selected.length} آدرس از نقشه سایت به لیست اضافه شد.`,
                    });
                  }}
                />
              )}
            </div>
            <Form.Control
              as="textarea"
              className="url-textarea mono"
              rows={5}
              placeholder={projectUrlListPlaceholder(project)}
              value={urlText}
              onChange={(e) => setUrlText(e.target.value)}
            />
            <div className="d-flex flex-wrap align-items-center gap-2 text-secondary small mt-2 mb-0">
              {formatFaNumber(uniqueUrlCount)} آدرس یکتا
            </div>
          </Form.Group>

          <Form.Group className="mb-3">
            <Form.Label className="mb-2">نوع تنظیمات</Form.Label>
            <div className="d-flex flex-wrap gap-3">
              <Form.Check
                type="radio"
                id="inspect-schedule-settings-default"
                name="inspect-schedule-settings-mode"
                label={RECOMMENDED_SETTINGS_LABEL}
                checked={settingsMode === 'default'}
                onChange={() => {
                  const defaults = applyDefaultInspectScheduleSettings();
                  setSettingsMode('default');
                  setWebhookEvents(defaults.events);
                  setWebhookFilters(defaults.filters);
                  setAutoIndexNotIndexed(defaults.autoIndexNotIndexed);
                  setMessengers([...DEFAULT_SCHEDULE_MESSENGERS]);
                }}
              />
              <Form.Check
                type="radio"
                id="inspect-schedule-settings-advanced"
                name="inspect-schedule-settings-mode"
                label="تنظیمات پیشرفته"
                checked={settingsMode === 'advanced'}
                onChange={() => setSettingsMode('advanced')}
              />
            </div>
            {settingsMode === 'default' && (
              <>
                <Alert variant="info" className="py-2 small mt-2 mb-0">
                  {RECOMMENDED_SETTINGS_ALERT}
                </Alert>
                <ScheduleChannelBotsAlert className="mt-2 mb-0" />
              </>
            )}
          </Form.Group>

          {settingsMode === 'advanced' && (
            <>
              <InspectScheduleWebhookForm
                events={webhookEvents}
                filters={webhookFilters}
                autoIndexNotIndexed={autoIndexNotIndexed}
                onEventsChange={setWebhookEvents}
                onFiltersChange={setWebhookFilters}
                onAutoIndexNotIndexedChange={setAutoIndexNotIndexed}
              />
              <ScheduleMessengerSelect
                value={messengers}
                onChange={setMessengers}
              />
            </>
          )}

          {!isNew && lastRunAt && (
            <p className="text-secondary small mb-0">
              آخرین اجرا: {formatTime(lastRunAt)}
              {lastRunStatus
                ? ` — ${jobStateLabel(lastRunStatus as ProjectJobState)}`
                : ''}
            </p>
          )}
        </Modal.Body>
        <Modal.Footer className="pt-3 d-flex flex-wrap align-items-center gap-2">
          {!isNew && selectedId && (
            <Button
              type="button"
              variant="outline-danger"
              disabled={busy}
              onClick={() => requestDelete(selectedId)}
            >
              حذف
            </Button>
          )}
          <div className="d-flex align-items-center flex-wrap gap-2 gap-md-3 ms-auto">
            <Form.Check
              type="switch"
              id="inspect-schedule-enabled"
              className="schedule-modal-enabled-switch mb-0"
              label="فعال (خودکار اجرا می‌شود)"
              checked={enabled}
              onChange={(e) => setEnabled(e.target.checked)}
            />
            <Button variant="outline-secondary" disabled={busy} onClick={closeModal}>
              انصراف
            </Button>
            <Button
              variant="falcon-primary"
              disabled={busy || !name.trim()}
              onClick={() => void onSave()}
            >
              {busy && (
                <Spinner
                  size="sm"
                  animation="border"
                  className="me-2"
                  style={{ width: 12, height: 12 }}
                />
              )}
              {isNew ? 'ساخت' : 'ذخیره'}
            </Button>
          </div>
        </Modal.Footer>
      </Modal>

      <ScheduleDeleteConfirmModal
        show={deleteConfirmOpen}
        scheduleName={
          schedules.find((item) => item.id === deleteTargetId)?.name ?? '—'
        }
        busy={deleteBusy}
        onConfirm={() => void confirmDelete()}
        onClose={closeDeleteConfirm}
        onExited={onDeleteModalExited}
      />
    </>
  );
}
